import type { AxiosRequestConfig } from "axios";
import { httpClient } from "@api/http";

/**
 * Typed wrappers over the shared http client. The backend answers with a flat envelope
 * (`{ ok, message, <payload keys…> }`), which the generic `ApiResponse<T>` wrapper cannot describe,
 * so every call states its own response shape instead.
 */
export const request = {
    get: <T>(url: string, config?: AxiosRequestConfig) =>
        httpClient.get<unknown>(url, config) as unknown as Promise<T>,
    post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
        httpClient.post<unknown>(url, data, config) as unknown as Promise<T>,
    patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
        httpClient.patch<unknown>(url, data, config) as unknown as Promise<T>,
    delete: <T>(url: string, config?: AxiosRequestConfig) =>
        httpClient.delete<unknown>(url, config) as unknown as Promise<T>,
    /** A file download (CSV): the raw text of the response. */
    text: (url: string) =>
        httpClient.get<unknown>(url, {
            responseType: "text",
            transformResponse: (d) => d,
        }) as unknown as Promise<string>,
};

/** Page of results, in the shape every list endpoint now shares (`data` + `meta`). */
export interface ListMeta {
    page: number;
    perPage: number;
    total: number;
    lastPage: number;
    from: number | null;
    to: number | null;
}

export interface Page<T> {
    items: T[];
    meta: ListMeta;
}

type ListEnvelope<T> = {
    data?: T[];
    meta?: Partial<ListMeta>;
    currentPage?: number;
    lastPage?: number;
    perPage?: number;
    total?: number;
    from?: number | null;
    to?: number | null;
} & Record<string, unknown>;

/** Reads the list out of an envelope, whichever of the legacy key or `data` it arrived under. */
export function toPage<T>(res: ListEnvelope<T>, legacyKey?: string): Page<T> {
    const legacy = legacyKey ? (res[legacyKey] as T[] | undefined) : undefined;
    const items = (Array.isArray(res.data) ? res.data : legacy) ?? [];
    const m = res.meta ?? {};
    return {
        items,
        meta: {
            page: Number(m.page ?? res.currentPage ?? 1),
            perPage: Number(m.perPage ?? res.perPage ?? items.length),
            total: Number(m.total ?? res.total ?? items.length),
            lastPage: Number(m.lastPage ?? res.lastPage ?? 1),
            from: m.from ?? res.from ?? null,
            to: m.to ?? res.to ?? null,
        },
    };
}

/** Builds `?a=1&b=2`, skipping empty values. */
export function qs(params: Record<string, unknown> | undefined): string {
    if (!params) return "";
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null || v === "") continue;
        sp.set(k, String(v));
    }
    const s = sp.toString();
    return s ? `?${s}` : "";
}
